import { X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { player } from '../audio/player';
import { navigate } from '../app/router';
import { toast } from '../components/Toast';
import { allItems, course, getItem, getLesson } from '../content';
import { dayKey } from '../lib/day';
import { pathOf } from '../lib/plan';
import { useNow } from '../lib/useNow';
import { advance, getState, skipScene, skipStep, useAppState } from '../state/store';
import type { Step } from '../state/model';
import { ContrastStep } from './ContrastStep';
import { HearStep } from './HearStep';
import { LessonDone } from './LessonDone';
import { LessonIntro } from './LessonIntro';
import { MeetStep } from './MeetStep';
import { SayStep } from './SayStep';
import { SceneStep } from './SceneStep';
import { SessionDone } from './SessionDone';

const itemExists = (id: string) => allItems.some((i) => i.id === id);

/** The named stages a session moves through, so the learner always knows where they are. */
const STAGE: Record<Step['kind'], string> = {
  review: 'Reviews',
  'lesson-intro': 'Scene',
  meet: 'Meet',
  contrast: 'Hear the difference',
  first: 'Check',
  scene: 'Role-play',
  'lesson-done': 'Lesson done',
  repeat: 'One more time',
};

/** Consecutive steps of the same stage, as segments of the progress bar. */
function stagesOf(steps: Step[]): Array<{ name: string; from: number; count: number }> {
  const out: Array<{ name: string; from: number; count: number }> = [];
  steps.forEach((step, i) => {
    const name = STAGE[step.kind];
    const last = out[out.length - 1];
    if (last && last.name === name) last.count++;
    else out.push({ name, from: i, count: 1 });
  });
  return out;
}

/**
 * A step can go stale: its phrase was flagged or retired, or its lesson has nothing left to
 * practise. Such steps are passed over.
 */
function isRunnable(step: Step): boolean {
  const s = getState();
  const open = (id: string) => itemExists(id) && !s.profile.flags[id];
  if ('cardId' in step) {
    const card = s.cards[step.cardId];
    return Boolean(card && open(card.itemId));
  }
  if (step.kind === 'meet') return open(step.itemId);
  if (step.kind === 'contrast') return step.itemIds.filter(open).length >= 2;
  const lesson = course.lessons.find((l) => l.id === step.lessonId);
  return Boolean(lesson && lesson.phrases.some(open));
}

export function Session() {
  const state = useAppState();
  const session = state.session;
  const heading = useRef<HTMLHeadingElement>(null);
  const step = session && session.index < session.steps.length ? session.steps[session.index] : null;
  const stepKey = session ? `${session.id}:${session.index}` : 'none';
  const now = useNow();
  // An unfinished session from an earlier day is left behind: today gets a fresh plan.
  const stale = Boolean(session && !session.completedAt && session.day !== dayKey(now));

  useEffect(() => {
    if (!session || stale) navigate('/', true);
  }, [session, stale]);

  useEffect(() => {
    const s = getState().session;
    if (!s || !step) return;
    // Passed over: stale steps, and a step already answered before the app was closed.
    if (!isRunnable(step) || s.answered?.includes(s.index)) advance(new Date(), s.index);
  }, [stepKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Move focus to the new step for keyboard and screen-reader users.
    heading.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [stepKey]);

  useEffect(() => () => player.stop(), []);

  // Lessons are numbered along the learner's own track.
  const lessonNumber = useMemo(() => new Map(pathOf(getState(), course).map((l, i) => [l.id, i + 1])), []);

  if (!session || stale) return null;
  const total = session.steps.length;
  const done = Boolean(session.completedAt);
  const at = session.index;
  const next = () => advance(new Date(), at);

  const leave = () => {
    player.stop();
    if (!done) toast('Saved. Pick up where you left off any time today.');
    navigate('/', true);
  };

  let body: React.ReactNode = null;
  if (done || !step) {
    body = <SessionDone session={session} />;
  } else if (isRunnable(step)) {
    switch (step.kind) {
      case 'lesson-intro': {
        const lesson = getLesson(step.lessonId);
        const newCount = session.steps.filter((s, i) => i > session.index && s.kind === 'meet' && getItem(s.itemId).lesson === lesson.id).length;
        body = <LessonIntro lesson={lesson} number={lessonNumber.get(lesson.id) ?? 1} newCount={newCount} onDone={next} />;
        break;
      }
      case 'meet':
        body = <MeetStep item={getItem(step.itemId)} onDone={next} quiet={session.quiet} />;
        break;
      case 'contrast':
        body = <ContrastStep items={step.itemIds.filter((id) => !state.profile.flags[id]).map(getItem)} onDone={next} quiet={session.quiet} />;
        break;
      case 'scene':
        body = <SceneStep lesson={getLesson(step.lessonId)} onDone={next} onSkip={() => skipScene(step.lessonId)} />;
        break;
      case 'lesson-done':
        body = <LessonDone lesson={getLesson(step.lessonId)} onDone={next} />;
        break;
      default: {
        const card = state.cards[step.cardId];
        const item = getItem(card.itemId);
        const mode = step.kind === 'review' ? 'review' : step.kind === 'first' ? 'first' : 'repeat';
        body =
          card.kind === 'say' ? (
            <SayStep card={card} item={item} mode={mode} onDone={next} onFlagged={next} />
          ) : (
            <HearStep card={card} item={item} mode={mode} onDone={next} onSkip={() => skipStep(new Date(), at)} onFlagged={next} />
          );
      }
    }
  }

  const position = Math.min(session.index, total);
  const stages = stagesOf(session.steps);
  const stage = stages.find((g) => position >= g.from && position < g.from + g.count);
  return (
    <div className="session">
      <header className="session-bar">
        <button type="button" className="icon-btn icon-btn-plain" onClick={leave} aria-label={done ? 'Close' : 'Leave the session (progress is saved)'}>
          <X size={24} aria-hidden="true" />
        </button>
        <div className="progress-wrap">
          <div
            className="progress"
            role="progressbar"
            aria-label="Session progress"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={position}
            aria-valuetext={done ? 'Finished' : `Step ${position + 1} of ${total}${stage ? `: ${stage.name}` : ''}`}
          >
            {stages.map((g) => {
              const filled = done ? 1 : Math.max(0, Math.min(1, (position - g.from) / g.count));
              return (
                <span key={g.from} className="progress-seg" style={{ flexGrow: g.count }} data-current={g === stage || undefined}>
                  <span style={{ width: `${filled * 100}%` }} />
                </span>
              );
            })}
          </div>
          <span className="progress-label" aria-hidden="true">
            {done ? 'Done' : stage ? (stage.count > 1 ? `${stage.name} · ${position - stage.from + 1} of ${stage.count}` : stage.name) : ''}
          </span>
        </div>
      </header>
      <main className="session-body" key={stepKey}>
        <h1 ref={heading} tabIndex={-1} className="sr-only">
          {done ? 'Session complete' : `Step ${position + 1} of ${total}`}
        </h1>
        {body}
      </main>
    </div>
  );
}
